import axios from "axios";
import { 
    AuthFlowType, 
    CognitoIdentityProviderClient, 
    InitiateAuthCommand, 
    RespondToAuthChallengeCommand, 
    RevokeTokenCommand,
    ForgotPasswordCommandInput,
    ForgotPasswordCommand,
    ConfirmSignUpCommand,
    ConfirmForgotPasswordCommandInput,
    ConfirmForgotPasswordCommand,
    GetUserCommand
} from "@aws-sdk/client-cognito-identity-provider";
import { API_BASE_URL, AUTH_API_BASE_URL } from "../services/apiConfig";

// Interfaccia per le informazioni utente decodificate
export interface UserInfo {
    sub: string; // userId univoco Cognito
    email: string;
    email_verified?: boolean;
    phone_number?: string;
    'custom:ragione_sociale'?: string;
    'custom:partita_iva'?: string;
    'custom:codice_fiscale'?: string;
    'custom:pec'?: string;
}

export class WebService {

    private data: any;
    private callbackFunction: (err: any, data: any) => void;
    private url: string;
    private method: string;
    private retry: Boolean = true;

    constructor(callbackFunction: (err: any, data: any) => void, data: any, url: string, method?: string) {
        this.data = data;
        this.callbackFunction = callbackFunction;
        this.url = url;
        this.method = method ?? "get";
    }

    async execute() {
        // Emetti evento di inizio caricamento per loader globale
        window.dispatchEvent(new CustomEvent('api:loading:start'));
        
        try {
            // I token sono gestiti esclusivamente dai cookie HttpOnly.
            let config = {
                method: this.method,
                url: encodeURI(this.url),
                headers: {
                    'Content-Type': 'application/json'
                },
                data: this.data,
                withCredentials: true // Mantiene le cookie per altre funzionalità
            };

            let response = await axios(config).catch((e: any) => {
                // In caso di 401, potrebbe essere necessario refresh token
                this.callbackFunction(e.message, null);
            });
        
            if(response !== undefined){
                this.callbackFunction(null, response.data);
            }
        } finally {
            // Emetti evento di fine caricamento (sempre, anche in caso di errore)
            window.dispatchEvent(new CustomEvent('api:loading:end'));
        }
    }

}

export async function checkStatusUser(_data: any, callback: (err: any, data: any) => void) {
    let user = new WebService(callback, _data, `${API_BASE_URL}/user/check_status_user`);
    user.execute();
}

export async function getUserActivities(callback: (err: any, data: any) => void) {
    let activities = new WebService(callback, null, `${API_BASE_URL}/user/activities`);
    activities.execute();
}

export async function createPayment(_data: any, callback: (err: any, data: any) => void) {
    let user = new WebService(callback, _data, `${API_BASE_URL}/user/create_payment`, "post");
    user.execute();
}

export async function insertUserAWS(_data: any, callback: (err: any, data: any) => void) {
    let user = new WebService(callback, _data, `${API_BASE_URL}/user/insert`, "post");
    user.execute();
}

export async function getDocsAWS(callback: (err: any, data: any) => void) {
    // Usa nuova API document management con httpOnly cookies
    let docs = new WebService(callback, null, `${API_BASE_URL}/user/document/list`);
    docs.execute();
}

export async function getDocumentPresignedAWS(
    _filename: string, 
    _mime_type: string, 
    _id_activity: string, 
    _id_work?: string | null,
    _slot_id?: string | null,
    callback?: (err: any, data: any) => void
) {
    // Usa nuova API per ottenere presigned URL per upload (versioning rimosso, aggiunto supporto Works)
    const body: any = { 
        filename: _filename, 
        mime_type: _mime_type, 
        id_activity: _id_activity
    };
    
    if (_id_work) body.id_work = _id_work;
    if (_slot_id) body.slot_id = _slot_id;
    
    let getpre = new WebService(callback!, body, `${API_BASE_URL}/user/document/presigned-upload`, "post");
    getpre.execute();
}

export async function getDocumentURLAWS(_id: string, callback: (err: any, data: any) => void) {
    // Usa nuova API document management con presigned URL
    let getdoc = new WebService(callback, null, `${API_BASE_URL}/user/document/presigned-download?id_document=${_id}`);
    getdoc.execute();
}

export async function uploadFileAWS(_presignedUrl: string, _document: File, callback: (err: any, data: any) => void) {
    try {
        // Upload diretto a S3 tramite presigned URL usando fetch (più pulito di axios)
        // Fetch non aggiunge header extra che invaliderebbero la firma del presigned URL
        const response = await fetch(_presignedUrl, {
            method: 'PUT',
            body: _document,
            headers: {
                'Content-Type': _document.type
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ [S3 UPLOAD] Errore:', response.status, errorText);
            callback(`Upload S3 fallito: ${response.status} ${response.statusText}`, null);
            return;
        }

        console.log('✅ [S3 UPLOAD] File caricato con successo');
        callback(null, { status: response.status, ok: response.ok });
    } catch (e: any) {
        console.error('❌ [S3 UPLOAD] Eccezione:', e);
        callback(e.message, null);
    }
}

export async function insertDocumentAWS(_fileObject: any, callback: (err: any, data: any) => void) {
    // Usa nuova API document management per salvare metadata dopo upload S3
    // _fileObject può contenere: id_document, name, description, id_activity, mime_type, path, id_work, slot_id, slot_name, slot_order
    let insdoc = new WebService(callback, _fileObject, `${API_BASE_URL}/user/document/insert`, "post");
    insdoc.execute();
}

export async function deleteDocumentsAWS(_id_document: string, callback: (err: any, data: any) => void) {
    // Usa nuova API document management per eliminare da S3 e DynamoDB
    let deldoc = new WebService(callback, null, `${API_BASE_URL}/user/document/delete?id_document=${_id_document}`, "delete");
    deldoc.execute();
}

// ==================== DOCUMENT MODELS CRUD ====================

export async function getDocumentModelsAWS(callback: (err: any, data: any) => void) {
    // Ottiene lista modelli documenti attivi ordinati per display_order
    let models = new WebService(callback, null, `${API_BASE_URL}/user/document-models/list`);
    models.execute();
}

export async function getDocumentModelDetailAWS(_id_model: string, callback: (err: any, data: any) => void) {
    // Ottiene dettaglio modello con template completo (slot_id, slot_name, order, is_required)
    let modelDetail = new WebService(callback, null, `${API_BASE_URL}/user/document-models/detail?id_model=${_id_model}`);
    modelDetail.execute();
}

// ==================== WORKS CRUD ====================

export async function createWorkAWS(_data: any, callback: (err: any, data: any) => void) {
    // Crea nuovo work copiando template da DocumentModel
    // _data: { work_name, id_activity, id_model, work_date?, description?, status? }
    let createWork = new WebService(callback, _data, `${API_BASE_URL}/user/works/create`, "post");
    createWork.execute();
}

export async function getWorksListAWS(_id_activity: string, callback: (err: any, data: any) => void) {
    // Lista lavori per activity (query via ActivityIndex GSI)
    let worksList = new WebService(callback, null, `${API_BASE_URL}/user/works/list?id_activity=${_id_activity}`);
    worksList.execute();
}

export async function getWorkDetailAWS(_id_work: string, callback: (err: any, data: any) => void) {
    // Dettaglio work con checklist completa e completion_percentage
    let workDetail = new WebService(callback, null, `${API_BASE_URL}/user/works/detail?id_work=${_id_work}`);
    workDetail.execute();
}

export async function updateWorkAWS(_data: any, callback: (err: any, data: any) => void) {
    // Aggiorna work (work_name, description, status, work_date)
    // _data: { id_work, work_name?, description?, status?, work_date? }
    let updateWork = new WebService(callback, _data, `${API_BASE_URL}/user/works/update`, "put");
    updateWork.execute();
}

export async function deleteWorkAWS(_id_work: string, callback: (err: any, data: any) => void) {
    // Elimina work (solo se completion_count = 0)
    let deleteWork = new WebService(callback, null, `${API_BASE_URL}/user/works/delete?id_work=${_id_work}`, "delete");
    deleteWork.execute();
}

/**
 * Crea un nuovo utente in Cognito utilizzando AdminCreateUserCommand.
 * * @param {string} username - L'username (o email) dell'utente.
 * @param {string} email - L'email dell'utente.
 * @param {string} [tempPassword] - Password temporanea opzionale.
 * @param {boolean} [shouldForceChange] - Forza il cambio password al primo login.
 */

export async function createNewCognitoUser(email: string, tempPassword: string, ragioneSociale: string, pIva: string, codFiscale: string, phone: string, pec: string, sedeLegale: Record<string, string>, callback: (err: any, data: any) => void) {

    if (!email.trim() || !tempPassword || !ragioneSociale.trim() || !pIva.trim() || !codFiscale.trim() || !phone.trim() || !pec.trim() || Object.values(sedeLegale).some(value => !value.trim())) {
        callback(new Error("Tutti i dati di registrazione sono obbligatori"), null);
        return;
    }

    fetch(`${AUTH_API_BASE_URL}/user/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password: tempPassword, ragioneSociale, partitaIva: pIva, codiceFiscale: codFiscale, phone, pec, sedeLegale })
    }).then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw Object.assign(new Error(data.error || 'Registrazione fallita'), { fields: data.fields, code: data.code });
        callback(null, data);
    }).catch((error) => callback(error, null));

}

export async function selfRegistrationAWS(_email: string, _password: string, callback: (err: any, data: any) => void) {

    const client = new CognitoIdentityProviderClient({
      //  apiVersion: '2016-04-18',
        region: 'eu-central-1'
    });
    const command = new InitiateAuthCommand({
        AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
        AuthParameters: {
            USERNAME: _email,
            PASSWORD: _password
        },
        ClientId: process.env.REACT_APP_CLIENT_ID,
    });
    
    try {
        const response = await client.send(command);
        callback(null, response);
    } catch (error) {
        callback(error, null);
    }
}

export async function loginAWS(_email: string, _password: string, callback: (err: any, data: any) => void) {

    const client = new CognitoIdentityProviderClient({
      //  apiVersion: '2016-04-18',
        region: 'eu-central-1'
    });
    const command = new InitiateAuthCommand({
        AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
        AuthParameters: {
            USERNAME: _email,
            PASSWORD: _password
        },
        ClientId: process.env.REACT_APP_CLIENT_ID,
    });
    
    try {
        const response = await client.send(command);
        callback(null, response);
    } catch (error) {
        callback(error, null);
    }
}

// refreshTokenAWS è stata rimossa - ora usiamo refreshTokenWithCookie() in authService.ts
// che gestisce il refresh token tramite cookie HttpOnly invece di sessionStorage

export async function respondToChallenge(_obj: any, _session: string, callback: (err: any, data: any) => void) {
    const client = new CognitoIdentityProviderClient({
        //apiVersion: '2016-04-18',
        region: 'eu-central-1'
    });
    const command = new RespondToAuthChallengeCommand({
        Session: _session,
        ChallengeName: "NEW_PASSWORD_REQUIRED",
        ClientId: process.env.REACT_APP_CLIENT_ID,
        ChallengeResponses: _obj,
    });

    try {
        const response = await client.send(command);
        callback(null, response);
    } catch (error) {
        callback(error, null);
    }
}

export async function logout(_RefreshToken: string, callback: (err: any, data: any) => void) {
    const client = new CognitoIdentityProviderClient({
        //apiVersion: '2016-04-18',
        region: 'eu-central-1'
    });
    const command = new RevokeTokenCommand({
        Token: _RefreshToken,
        ClientId: process.env.REACT_APP_CLIENT_ID
    });
    
    try {
        const response = await client.send(command);
        callback(null, response);
    } catch (error) {
        callback(error, null);
    }
}

export async function confirmCode(_obj: any, callback: (err: any, data: any) => void) {
    const client = new CognitoIdentityProviderClient({
        region: 'eu-central-1'
    });
    const command = new ConfirmSignUpCommand({
        Username: _obj.Username,
        ConfirmationCode: _obj.ConfirmationCode,
        ClientId: process.env.REACT_APP_CLIENT_ID,
    });

    try {
        const response = await client.send(command);
        callback(null, response);
    } catch (error) {
        callback(error, null);
    }
}

export async function requestPasswordReset(username: string, callback: (err: any, data: any) => void) {
    const client = new CognitoIdentityProviderClient({
        region: 'eu-central-1'
    });
    const params: ForgotPasswordCommandInput = {
        ClientId: process.env.REACT_APP_CLIENT_ID,
        Username: username.trim(),
    };

    const command = new ForgotPasswordCommand(params);

    try {
        const response = await client.send(command);
        callback(null, response);
        console.log("Codice di reset inviato con successo.");
    } catch (error) {
        console.error("Errore nella richiesta di reset:", error);
        throw error;
    }
}

export async function confirmPasswordReset(
    username: string, 
    confirmationCode: string, 
    newPassword: string,
    callback: (err: any, data: any) => void) 
{
    const client = new CognitoIdentityProviderClient({
        region: 'eu-central-1'
    });
    
    const params: ConfirmForgotPasswordCommandInput = {
        ClientId: process.env.REACT_APP_CLIENT_ID,
        Username: username.trim(),
        ConfirmationCode: confirmationCode,
        Password: newPassword, // Deve rispettare i criteri di complessità del User Pool
    };

    const command = new ConfirmForgotPasswordCommand(params);

    try {
        const response = await client.send(command);
        callback(null, response);
        console.log("Password resettata con successo. L'utente può ora loggarsi.");
    } catch (error) {
        console.error("Errore nella conferma del reset:", error);
        throw error;
    }
}

/**
 * Decodifica l'IdToken JWT per estrarre le informazioni utente (claims)
 * ATTENZIONE: Questo è solo per lettura dei dati, NON per validazione sicurezza!
 * La validazione deve essere fatta backend-side.
 */
export function decodeIdToken(idToken: string): UserInfo | null {
    try {
        // JWT è formato: header.payload.signature
        const parts = idToken.split('.');
        if (parts.length !== 3) {
            console.error('Token JWT malformato');
            return null;
        }
        
        // Decodifica il payload (parte centrale)
        const payload = parts[1];
        const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        const claims = JSON.parse(decodedPayload);
        
        return {
            sub: claims.sub,
            email: claims.email,
            email_verified: claims.email_verified,
            phone_number: claims.phone_number,
            'custom:ragione_sociale': claims['custom:ragione_sociale'],
            'custom:partita_iva': claims['custom:partita_iva'],
            'custom:codice_fiscale': claims['custom:codice_fiscale'],
            'custom:pec': claims['custom:pec']
        };
    } catch (error) {
        console.error('Errore decodifica IdToken:', error);
        return null;
    }
}

/**
 * Recupera le informazioni complete dell'utente usando l'AccessToken
 * Questo metodo fa una chiamata API a Cognito per ottenere tutti gli attributi
 */
export async function getUserInfo(accessToken: string, callback: (err: any, data: any) => void) {
    const client = new CognitoIdentityProviderClient({
        region: 'eu-central-1'
    });
    
    const command = new GetUserCommand({
        AccessToken: accessToken
    });
    
    try {
        const response = await client.send(command);
        
        // Trasforma gli attributi in un oggetto più usabile
        const userInfo: any = {
            username: response.Username,
            sub: ''
        };
        
        response.UserAttributes?.forEach(attr => {
            if (attr.Name === 'sub') {
                userInfo.sub = attr.Value;
            } else if (attr.Name === 'email') {
                userInfo.email = attr.Value;
            } else if (attr.Name === 'email_verified') {
                userInfo.email_verified = attr.Value === 'true';
            } else if (attr.Name === 'phone_number') {
                userInfo.phone_number = attr.Value;
            } else if (attr.Name?.startsWith('custom:')) {
                userInfo[attr.Name] = attr.Value;
            }
        });
        
        callback(null, userInfo);
    } catch (error) {
        console.error('Errore recupero info utente:', error);
        callback(error, null);
    }
}